import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';
import BarChartIcon from '@mui/icons-material/BarChart';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import SavingsIcon from '@mui/icons-material/Savings';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import {
  getProgramOverviewSummary,
  getProgramManagementAttentionList,
  getProgramOeeActualVsPotential,
} from '../../../Services/app/oeeservice';
import OverviewPage2 from './OverviewPage2';

const COLOR_RED = '#d03b3b';
const COLOR_ORANGE = '#eb6834';
const COLOR_AMBER = '#e0a11a';
const COLOR_GREEN = '#0ca30c';
const COLOR_BLUE = '#2a78d6';
const COLOR_PURPLE = '#7e4fd1';
const NAVY = '#0d2a5c';
const BORDER = '#ECEFF3';
const TEXT_PRIMARY = '#0b0b0b';
const TEXT_MUTED = '#898781';

const StatusBox = ({ children }) => (
  <Box
    sx={{
      p: 6,
      border: `1px solid ${BORDER}`,
      borderRadius: 2,
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: TEXT_MUTED,
      gap: 1,
    }}
  >
    {children}
  </Box>
);

const secondsToHours = (totalSeconds) => (totalSeconds || 0) / 3600;

const secondsToHms = (totalSeconds) => {
  if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) return '—';
  const abs = Math.abs(Math.round(totalSeconds));
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const seconds = abs % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const fmtCost = (value) => `₹${Math.round(value || 0).toLocaleString('en-IN')}`;
const fmtHrs = (totalSeconds) => `${secondsToHours(totalSeconds).toFixed(1)} hrs`;

const IconCircle = ({ icon, color, size = 44 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: `${color}1a`,
      color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {icon}
  </Box>
);

const formatMachineLabel = (machines) => {
  if (!Array.isArray(machines) || machines.length === 0) return '';
  if (machines.length <= 2) return machines.join(', ');
  return `${machines.slice(0, 2).join(', ')} +${machines.length - 2} more`;
};

const RecoveryHero = ({ summary, periodLabel, customerName, machines }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${NAVY} 0%, #123a7a 100%)`,
      borderRadius: 2,
      color: '#fff',
      p: 3,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 3,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '2 1 340px' }}>
      <IconCircle icon={<SavingsIcon />} color="#8fd7ff" size={56} />
      <Box>
        <Typography sx={{ fontSize: '11px', letterSpacing: '0.06em', color: '#b8c6e6', fontWeight: 600 }}>
          THIS {periodLabel ? periodLabel.toUpperCase() : ''} RECOVERY OPPORTUNITY
        </Typography>
        {(customerName || (machines && machines.length > 0)) && (
          <Typography sx={{ fontSize: '11px', color: '#8fd7ff', fontWeight: 600, mb: 0.25 }}>
            {[customerName, formatMachineLabel(machines)].filter(Boolean).join(' · ')}
          </Typography>
        )}
        <Typography sx={{ fontSize: '34px', fontWeight: 700, color: '#8fe08f', lineHeight: 1.2 }}>
          {fmtCost(summary.combined_recovery)}
        </Typography>
        <Typography sx={{ fontSize: '12px', color: '#dfe6f5' }}>Total recoverable opportunity</Typography>
        <Typography sx={{ fontSize: '11px', color: '#b8c6e6', mt: 0.5 }}>
          minimize idle time and gain more production and profit
        </Typography>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', gap: 3, flex: '1 1 260px', borderLeft: { sm: '1px solid #3a568f' }, pl: { sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <AccessTimeIcon sx={{ color: '#8fd7ff', fontSize: 20, mt: 0.3 }} />
        <Box>
          <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>{fmtHrs(summary.extra_idle1)}</Typography>
          <Typography sx={{ fontSize: '11px', color: '#b8c6e6' }}>Excess idle time</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Inventory2OutlinedIcon sx={{ color: '#8fd7ff', fontSize: 20, mt: 0.3 }} />
        <Box>
          <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>{summary.extra_part_count} parts</Typography>
          <Typography sx={{ fontSize: '11px', color: '#b8c6e6' }}>Additional production opportunity</Typography>
        </Box>
      </Box>
    </Box>
  </Box>
);

const StatCard = ({ icon, color, title, value, valueColor, subtitle, footnote }) => (
  <Box
    sx={{
      flex: '1 1 220px',
      border: `1px solid ${BORDER}`,
      borderRadius: 2,
      background: '#ffffff',
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 1,
    }}
  >
    <IconCircle icon={icon} color={color} />
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: TEXT_PRIMARY }}>{title}</Typography>
    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: valueColor || color }}>{value}</Typography>
    <Typography sx={{ fontSize: '12px', color: TEXT_MUTED }}>{subtitle}</Typography>
    {footnote && (
      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: valueColor || color }}>{footnote}</Typography>
    )}
  </Box>
);

const AttentionRow = ({ icon, color, index, title, description, badges }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2,
      py: 2,
      borderBottom: `1px solid ${BORDER}`,
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <IconCircle icon={icon} color={color} />
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: TEXT_PRIMARY }}>
        {index}. {title}
      </Typography>
      <Typography sx={{ fontSize: '13px', color: TEXT_MUTED, mt: 0.25 }}>{description}</Typography>
    </Box>
    {badges && badges.length > 0 && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 190 }}>
        {badges.map((b) => (
          <Box key={b.label} sx={{ background: `${b.color}14`, borderRadius: 1.5, px: 2, py: 1, textAlign: 'right' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: b.color, letterSpacing: '0.03em' }}>
              {b.label.toUpperCase()}
            </Typography>
            {b.lines.map((line, i) => (
              <Typography key={i} sx={{ fontSize: '12px', fontWeight: 600, color: TEXT_PRIMARY }}>{line}</Typography>
            ))}
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

// "if available means or don't show anything" - callers pass through a real
// array/object; badges with nothing to show are simply omitted, never
// rendered with a placeholder dash.
// 6-point management-attention-list shape: total_idle_hours, top_idle_reason,
// top_alarm, cycle_speed_extremes, top_idle_component, unallocated_machine_time
// (conditional). Each point is its own flat item now - no nested
// top_reasons/top_alarms arrays.
const buildAttentionRows = (items) => {
  const byKey = Object.fromEntries((items || []).map((i) => [i.key, i]));

  const totalIdle = byKey.total_idle_hours;
  const topReason = byKey.top_idle_reason;
  const topAlarm = byKey.top_alarm;
  const extremes = byKey.cycle_speed_extremes;
  const topJob = byKey.top_idle_job;

  const rows = [];

  if (totalIdle) {
    rows.push({
      icon: <AccessTimeIcon />,
      color: COLOR_RED,
      title: 'Total Idle Hours',
      description: `${fmtHrs(totalIdle.idle_time)} of total idle time across the selected machines.`,
      badges: [{ label: 'Idle Time', color: COLOR_RED, lines: [fmtHrs(totalIdle.idle_time)] }],
    });
  }

  if (topReason && topReason.name) {
    rows.push({
      icon: <AccessTimeIcon />,
      color: COLOR_RED,
      title: `Top Idle Reason: ${topReason.name}`,
      description: `${secondsToHms(topReason.total_idle_duration)} of idle time across ${topReason.count} occurrence(s).`,
      badges: [{ label: 'Idle Time', color: COLOR_RED, lines: [secondsToHms(topReason.total_idle_duration)] }],
    });
  }

  if (topAlarm && topAlarm.alarm_message) {
    rows.push({
      icon: <NotificationsActiveIcon />,
      color: COLOR_AMBER,
      title: `Top Alarm: ${topAlarm.alarm_message.trim()}`,
      description: `${secondsToHms(topAlarm.total_alarm_duration)} of alarm time across ${topAlarm.count} occurrence(s).`,
      badges: [{ label: 'Alarm Time', color: COLOR_AMBER, lines: [secondsToHms(topAlarm.total_alarm_duration)] }],
    });
  }

  if (extremes) {
    rows.push({
      icon: <MonitorHeartIcon />,
      color: COLOR_BLUE,
      title: 'Cycle Time Extremes',
      description: `${extremes.total_out_of_range} cycles ran outside target speed (${extremes.low_cycle} low speed, ${extremes.high_cycle} high speed) which can affect product quality.`,
      badges: [{ label: 'Total Out-of-Range Cycles', color: COLOR_BLUE, lines: [`${extremes.total_out_of_range}`] }],
    });
  }

  if (topJob && topJob.job_name) {
    rows.push({
      icon: <AccessTimeIcon />,
      color: COLOR_RED,
      title: `Top Idle Job: ${topJob.job_name}`,
      description: `${topJob.machine_name} · ${fmtHrs(topJob.extra_idle1)} of net extra idle time, ${fmtCost(topJob.idle_cost)} of lost potential.`,
      badges: [{ label: 'Idle Time', color: COLOR_RED, lines: [fmtHrs(topJob.extra_idle1), fmtCost(topJob.idle_cost)] }],
    });
  }

  return rows;
};

// Method 3 overview - the Insights1 program-report pipeline ported in as-is:
// machine_name + start_time/end_time window based API calls instead of
// shift/date/method based ones. Reuses OverviewPage2 unchanged since the
// program-report summary shape is otherwise identical to the component-report
// one (idle_cost is aliased to extra_hour_cost below to keep it that way).
export default function ProgramOverviewTab({ filters, periodLabel, customerName }) {
  const [summary, setSummary] = useState(null);
  const [attentionItems, setAttentionItems] = useState(null);
  const [oee, setOee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!filters) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const [summaryData, attentionData, oeeData] = await Promise.all([
          getProgramOverviewSummary(filters.machines, filters.startTimeMs, filters.endTimeMs),
          getProgramManagementAttentionList(filters.machines, filters.startTimeMs, filters.endTimeMs),
          getProgramOeeActualVsPotential(filters.machines, filters.startTimeMs, filters.endTimeMs),
        ]);
        if (!cancelled) {
          setSummary(summaryData ? { ...summaryData, extra_hour_cost: summaryData.idle_cost } : null);
          setAttentionItems(attentionData ? attentionData.items : null);
          setOee(oeeData || null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch program report overview', err);
          setError('Failed to load overview. Please try again.');
          setSummary(null);
          setAttentionItems(null);
          setOee(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filters]);

  if (!filters) {
    return <StatusBox>Select filters and click Submit to load the overview.</StatusBox>;
  }

  if (loading) {
    return (
      <StatusBox>
        <CircularProgress size={28} />
      </StatusBox>
    );
  }

  if (error) {
    return <StatusBox>{error}</StatusBox>;
  }

  if (!summary) {
    return <StatusBox>No data for the selected filters.</StatusBox>;
  }

  const potentialParts = summary.actual + summary.extra_part_count;
  const potentialPct = summary.actual ? (summary.extra_part_count / summary.actual) * 100 : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <RecoveryHero summary={summary} periodLabel={periodLabel} customerName={customerName} machines={filters?.machines} />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <StatCard
          icon={<AccessTimeIcon />}
          color={COLOR_RED}
          title="Idle Time Loss"
          value={fmtCost(summary.extra_hour_cost)}
          subtitle="Recoverable from excess idle time"
          footnote={fmtHrs(summary.extra_idle)}
        />
        <StatCard
          icon={<SpeedIcon />}
          color={COLOR_ORANGE}
          title="Production Loss"
          value={fmtCost(summary.extra_part_cost)}
          subtitle="Lost due to below-target speed"
          footnote={`${summary.extra_part_count} parts`}
        />
        <StatCard
          icon={<BarChartIcon />}
          color={COLOR_GREEN}
          title="Potential Production Improvement"
          value={`+${potentialPct.toFixed(1)}%`}
          subtitle="Increase in production potential"
          footnote={`${summary.actual} → ${potentialParts} parts`}
        />
        <StatCard
          icon={<GpsFixedIcon />}
          color={COLOR_PURPLE}
          title="Current vs Target"
          value={`${summary.attainment_pct}%`}
          subtitle="Target attainment"
          footnote={`${summary.actual} of ${summary.target} parts`}
        />
      </Box>

      <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, background: '#ffffff', p: 3 }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: TEXT_PRIMARY, mb: 1 }}>
          WHAT NEEDS MANAGEMENT ATTENTION?
        </Typography>
        {attentionItems && attentionItems.length > 0 ? (
          buildAttentionRows(attentionItems).map((row, idx) => <AttentionRow key={row.title} index={idx + 1} {...row} />)
        ) : (
          <Typography sx={{ fontSize: '13px', color: TEXT_MUTED }}>No attention items for the selected filters.</Typography>
        )}
      </Box>

      <OverviewPage2 summary={summary} attentionItems={attentionItems} oee={oee} />
    </Box>
  );
}
